/**
 * HomePage — 홈 (PKA 화면정의서 SC-019 · 기능명세 FN-046~051 · PRD §4.3).
 *
 * 진입 첫 탭. 4개 섹션으로 구성한다:
 *   §1 내 보유 카드      — 급등락 배너(TopMoverBanner) **하나뿐** (FN-047)
 *       IA SC-019 §1 "⚠ 구현: 미리보기 캐러셀 대신 급등락 배너 단일화(닫으면 이번 방문
 *       한정 §1 숨김)". 2026-08-24 에 옛 기능명세를 근거로 캐러셀을 되살렸다가 2026-09-07
 *       IA·design 실물에 맞춰 다시 걷었다(사용자 결정). 배너 자체가 컬렉션으로 가는
 *       문이라 제목·'전체 보기'도 두지 않는다 — 같은 자리에 문이 둘이 된다.
 *   §2 요즘 많이 모으는  — 실 트렌딩(GET /cards/trending) 칩 3종 + 더보기 (FN-048)
 *   §3 내 관심 카드      — **컬렉션에 담은 카드** = 관심 카드 (FN-049)
 *       SC-019 v0.4 rev2·rev3(2026-08-18): 별도 관심/찜 지정 기능은 **없다**. 컬렉션에
 *       담는 행위가 곧 관심 표시다. 카드 캐러셀만 둔다 — 함께 내던 컬렉션 요약
 *       (카드수·시세합계·평가손익)은 2026-09-17 에 걷었다(컬렉션 탭이 같은 숫자를
 *       화면 머리말로 이미 말한다).
 *       ⚠ 소스는 **보유가 아니라 컬렉션**이다 (2026-09-17). IA rev27 이 보유를
 *         "스캔뿐" 으로 좁히면서 카드 상세·트렌딩에서 담은 카드는 보유가 되지 않는다 —
 *         보유를 읽으면 방금 담은 카드가 홈에 안 뜬다. 묶음을 가리지 않고 최근 담은
 *         순으로 받는다(GET /collection/groups/cards/recent). 스캔한 카드는 §1 배너가
 *         맡는다.
 *   §4 최근 스캔한 카드  — 로컬 기록(lib/recentScans) + batch 조회 **행 캐러셀** (FN-050)
 *       한 칸이 본문 폭을 다 써 모바일에서 한 번에 한 건. 행의 좌/우 구조
 *       (썸네일 · 이름 · 가격)는 목록 때 그대로다.
 *
 * 전역 상태 (FN-051 · IA §4.3):
 *   - 신뢰도 낮음·산정 불가는 숫자를 단정하지 않는다
 *   - 등락 색상은 서구식(▲초록/▼빨강) — TrendChip 이 담당
 *   - §1 은 **보유 배리언트** 기준 시세, §2 는 선택 에디션 기준
 *
 * ⚠ "시세 갱신 N분 전" 상시 노출(FN-051)은 **아직 없다** — 수집 시각을 주는 API 가 없어
 * 지어낸 숫자를 걸 수는 없다. 시세 응답에 수집 시각이 실리면 그때 단다.
 *
 * ⚠ §2 트렌딩은 시세 기반 v1 근사치다(api/card.ts TrendChipKey 참조). 가격 미적재
 * 환경에서는 빈 목록이 정답이라 빈 상태가 뜬다.
 */
import { useMemo, useState, useSyncExternalStore } from "react";
import { useNavigate } from "react-router-dom";
import { useMoney } from "../../hooks/useMoney";
import { useOverlayNavigate } from "../../hooks/useOverlayNavigate";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@astryxdesign/core/Button";
import { Card } from "@astryxdesign/core/Card";
import { Carousel } from "@astryxdesign/core/Carousel";
import { ClickableCard } from "@astryxdesign/core/ClickableCard";
import { IconButton } from "@astryxdesign/core/IconButton";
import { Item } from "@astryxdesign/core/Item";
import { List } from "@astryxdesign/core/List";
import { Section } from "@astryxdesign/core/Section";
import { Skeleton } from "@astryxdesign/core/Skeleton";
import { Heading } from "@astryxdesign/core/Heading";
import { Text } from "@astryxdesign/core/Text";
import { ToggleButton } from "@astryxdesign/core/ToggleButton";
import { VStack, HStack } from "@astryxdesign/core/Layout";
import CardThumb from "../../components/card/CardThumb";
import TrendChip from "../../components/card/TrendChip";
import Icon from "../../components/primitives/Icon";
import SwipeTabPanel from "../../components/primitives/SwipeTabPanel";
import TrendingAllSheet from "../../components/home/TrendingAllSheet";
import AddToCollectionSheet from "../../components/collection/AddToCollectionSheet";
import { useRequireAuth } from "../../navigation/useRequireAuth";
import TopMoverBanner from "../../components/home/TopMoverBanner";
import { useEdition } from "../../components/edition/EditionContext";
import EditionSelector from "../../components/edition/EditionSelector";
import {
  cardImageUrl,
  fetchCardsByIds,
  type CardItem,
  type PriceView,
  type TrendChipKey,
} from "../../api/card";
import type { CollectionGroupCard } from "../../api/collectionGroup";
import { useCollectionQuery } from "../../hooks/useCollectionQueries";
import { useRecentCollectionCardsQuery } from "../../hooks/useCollectionGroupQueries";
import { useTrendingQuery } from "../../hooks/useCardQueries";
import { listRecentScans, subscribeRecentScans } from "../../lib/recentScans";
import { useAuthStore } from "../../store/authStore";
import { maskPrice } from "../../lib/priceMasking";
import styles from "./HomePage.module.css";
import InFeedAd from "../../components/ads/InFeedAd";
import AppInstallBanner from "../../components/web/AppInstallBanner";
// ⏳ 광고 PoC (2026-09-09) — 개발 빌드에서만 렌더. 결론 나면 함께 지운다.
import GptFluidProbe from "../../dev/GptFluidProbe";

/** §2 에 상시 노출하는 개수. 나머지는 '더보기'(SC-019a). */
const TREND_PREVIEW_COUNT = 5;

/** §3 캐러셀이 내는 관심(=컬렉션) 카드 수. 나머지는 컬렉션 탭에서 본다. */
const WATCH_COUNT = 10;

/** §2 필터 칩 — 표시 순서. 기본은 '최근 핫한'. */
export const TREND_CHIPS: TrendChipKey[] = ["HOT", "GAINERS", "STEADY"];

export default function HomePage() {
  const { t } = useTranslation("card");
  const money = useMoney();
  const navigate = useNavigate();
  // 카드 상세·보유 폼은 activity 로 뜬다 — overlayNavigate 가 규칙을 보고 스택에 올린다.
  const overlayNavigate = useOverlayNavigate();
  const { edition } = useEdition();
  const isAuthenticated = useAuthStore((s) => !!s.accessToken);
  // 담기는 회원만 — 비회원은 로그인 오버레이부터 (§6.1). 종전엔 저장 폼 라우트의
  // RequireAuth 가 이 일을 했는데, 시트로 바뀌며 라우트를 안 타게 됐다.
  const { requireAuth } = useRequireAuth();
  const [chip, setChip] = useState<TrendChipKey>("HOT");
  // §2 '더보기' — 페이지 전환 없이 시트로 연다 (홈의 스크롤 위치를 잃지 않도록, design)
  const [isTrendSheetOpen, setIsTrendSheetOpen] = useState(false);
  /** 담기 시트 대상 — null 이면 닫힘 (design SC-012). */
  const [addSheetCard, setAddSheetCard] = useState<{ serialNo: number; name: string } | null>(null);

  const collection = useCollectionQuery(); // 비로그인은 자체적으로 호출 안 함
  const trending = useTrendingQuery(chip, edition, TREND_PREVIEW_COUNT);

  // §4 — 로컬 기록의 카드 ID 를 batch 로 조회. 카탈로그에서 빠진 카드는 응답에서
  // 조용히 빠진다(정상 경로). 홈은 keep-alive 탭이라 useMemo([]) 로 읽으면 스캔하고
  // 돌아와도 안 바뀐다 — 기록 모듈을 **구독**해서 새 스캔이 즉시 반영되게 한다.
  const recentIds = useSyncExternalStore(subscribeRecentScans, listRecentScans);
  const recentScans = useQuery({
    queryKey: ["cards", "recent-scans", recentIds],
    queryFn: async () => {
      const map = await fetchCardsByIds(recentIds);
      return recentIds.flatMap((id) => map.get(id) ?? []);
    },
    enabled: recentIds.length > 0,
    staleTime: 5 * 60_000,
  });

  const holdings = collection.data?.holdings;

  /**
   * §1·§3 공통 — 카드별 보유 수량. 같은 카드를 등급별로 나눠 담아도 합쳐서 센다.
   */
  const heldQtyByCard = useMemo(() => {
    const map = new Map<number, number>();
    for (const h of holdings ?? []) {
      map.set(h.card.cardSerialNo, (map.get(h.card.cardSerialNo) ?? 0) + h.quantity);
    }
    return map;
  }, [holdings]);

  /**
   * §3 관심 카드 = **컬렉션에 담은 카드** (SC-019 v0.4 rev2).
   *
   * 접기·정렬(최근 담은 순)은 서버가 한다 — 같은 카드를 두 컬렉션에 담는 것은 정상이고,
   * 앱에서 접으면 컬렉션 수만큼 왕복한 뒤에야 접을 수 있다.
   */
  const watchQuery = useRecentCollectionCardsQuery(WATCH_COUNT);
  const watchCards = watchQuery.data ?? [];

  const openCard = (cardSerialNo: number) => overlayNavigate(`/cards/${cardSerialNo}`);

  /**
   * 목록 시세 — 비회원은 마스킹(§4.2), 없으면 '시세 없음'(§5.4).
   * PriceView 를 직접 받는다 — 카탈로그 카드는 `listPrice`, 보유 건은 `currentPrice` 라
   * 담는 그릇이 다르지만 표시 규칙은 하나다.
   */
  const priceText = (price: PriceView): string | null => {
    if (price.display === "NONE" || price.amount === null) return null;
    return isAuthenticated ? money.format(price.amount, price.currency ?? undefined) : maskPrice(price.amount);
  };

  /**
   * §3 캐러셀 타일 — 아트워크 · 카드명 · 현재가 · 내 보유 수량.
   *
   * ⚠ 카드명은 **body(16px/400)** 다. label 이 아니다 — 이 테마에서 label 은
   *   16px/500 이라 굵기를 따로 덮어야 하고, §2 트렌드 행·§4 최근 스캔 행은
   *   Astryx Item 기본 400 이라 여기만 굵으면 같은 화면에서 같은 것(카드 이름)이
   *   두 무게로 보인다 (design 2026-08-19).
   */
  const carouselCard = (row: CollectionGroupCard) => {
    const { card } = row;
    const price = priceText(row.currentPrice);
    const heldQty = heldQtyByCard.get(card.cardSerialNo) ?? 0;
    return (
      <VStack key={card.cardSerialNo} width={132}>
        <ClickableCard
          label={card.name}
          variant="transparent"
          padding={0}
          onClick={() => openCard(card.cardSerialNo)}
        >
          <VStack gap={1}>
            <CardThumb imgUrl={cardImageUrl(card.imagePathway) ?? undefined} size="fill" />
            <Text type="body" maxLines={1} wordBreak="break-all">
              {card.name}
            </Text>
            {price === null ? (
              <Text type="supporting" color="secondary">{t("search.noPrice")}</Text>
            ) : (
              <Text type="label" weight="bold">{price}</Text>
            )}
            {/* 컬렉션에는 **아직 안 산 카드**도 들어간다(IA SC-016) — 그 카드에 '0장' 을
                적으면 없는 수량을 세는 말이 된다. 컬렉션 상세와 같은 문구로 가른다. */}
            <Text type="supporting" color="secondary">
              {heldQty > 0
                ? t("home.watchOwned", { count: heldQty })
                : t("collection.groupNotOwned")}
            </Text>
          </VStack>
        </ClickableCard>
      </VStack>
    );
  };

  /**
   * §4 캐러셀 한 칸 — 썸네일 · 카드명 · 세트/번호 · 현재가.
   *
   * ⚠ as="div" 다. 캐러셀 칸이라 더는 <ul> 안이 아니므로 li 로 두면 부모 없는
   *   목록 항목이 된다. 대신 칸마다 테두리를 줘서(.scanSlide) 어디까지가 한 건인지
   *   보인다 — 목록에서는 구분선(hasDividers)이 하던 일이다.
   */
  const recentScanSlide = (card: CardItem) => {
    const price = priceText(card.listPrice);
    return (
      <div key={card.cardSerialNo} className={styles.scanSlide}>
        <Item
          as="div"
          label={card.name}
          description={[card.cardsetCode.toUpperCase(), card.number].filter(Boolean).join(" ")}
          labelLines={1}
          onClick={() => openCard(card.cardSerialNo)}
          startContent={<CardThumb imgUrl={cardImageUrl(card.imagePathway) ?? undefined} />}
          endContent={
            price === null ? (
              <Text type="supporting" color="secondary">{t("search.noPrice")}</Text>
            ) : (
              <Text type="body" weight="bold">{price}</Text>
            )
          }
        />
      </div>
    );
  };

  /** §2 심플 뷰 한 줄 — 썸네일 · 세트코드+번호 · 등락 칩+현재가 (FN-048). */
  const trendRow = (card: CardItem) => {
    const price = priceText(card.listPrice);
    return (
      <Item
        key={card.cardSerialNo}
        as="li"
        label={card.name}
        description={[card.cardsetCode.toUpperCase(), card.number].filter(Boolean).join(" ")}
        labelLines={1}
        onClick={() => openCard(card.cardSerialNo)}
        startContent={<CardThumb imgUrl={cardImageUrl(card.imagePathway) ?? undefined} />}
        endContent={
          <VStack gap={0.5} align="end">
            <Text type="body" weight="bold">
              {price ?? t("search.noPrice")}
            </Text>
            <TrendChip value={card.listPrice.trendRate} />
          </VStack>
        }
      />
    );
  };

  return (
    <Section variant="transparent" padding={4} maxWidth={1120} width="100%" minHeight="100%">
      <VStack gap={6}>
        <AppInstallBanner />
        {/* 에디션 선택 (SC-009) — §2 트렌딩의 시세 기준.
            우측 새로고침 = FN-051 수동 갱신(실제 refetch). "N분 전" 라벨은 시세 응답에
            수집 시각이 실리기 전까지 달지 않는다 — 지어낸 숫자를 걸 수는 없다. */}
        <EditionSelector
          endContent={
            <IconButton
              label={t("home.refresh")}
              tooltip={t("home.refresh")}
              variant="ghost"
              size="sm"
              icon={<Icon name="Reload" size="md" />}
              clickAction={async () => {
                await Promise.all([
                  trending.refetch(),
                  collection.refetch(),
                  recentScans.refetch(),
                ]);
              }}
            />
          }
        />

        {/* ── §1 내 보유 카드 (FN-047) ─────────────────────────────
            보유가 있으면 **배너 하나뿐**이다 (IA SC-019 §1 · design 2026-08-18).
            배너를 닫으면 TopMoverBanner 가 null 을 돌려 §1 이 통째로 사라진다 — 그래서
            VStack 으로 감싸지 않는다(빈 VStack 이 남으면 위아래 섹션 사이 간격만 벌어진다).
            ⚠ 로딩 중에는 아무것도 그리지 않는다 — 빈 상태(온보딩)가 먼저 깜빡이면
            보유가 있는 사람에게 "등록한 카드가 없다"고 말하는 셈이 된다. */}
        {isAuthenticated && collection.isPending ? null : (holdings?.length ?? 0) > 0 ? (
          <TopMoverBanner />
        ) : (
          // 빈 상태(비로그인 포함) = 온보딩 + 발견 통합 — 등록 유도 · 인기 순위(실 트렌딩)
          <VStack gap={4}>
            <Card padding={5}>
              <VStack gap={3} align="start">
                <Text weight="semibold">{t("home.emptyHoldingTitle")}</Text>
                <Text type="supporting" color="secondary">{t("home.emptyHoldingBody")}</Text>
                <HStack gap={2} wrap="wrap">
                  <Button variant="primary" label={t("home.goScan")} clickAction={() => overlayNavigate("/scan")} />
                  <Button variant="secondary" label={t("home.addBySearch")} clickAction={() => navigate("/search")} />
                </HStack>
              </VStack>
            </Card>

            {trending.isSuccess && trending.data.length > 0 && (
              <VStack gap={2}>
                <Text weight="semibold">{t("home.popularRanking")}</Text>
                <List hasDividers>
                  {trending.data.map((card, i) => (
                    <Item
                      key={card.cardSerialNo}
                      as="li"
                      label={card.name}
                      description={[card.cardsetCode.toUpperCase(), card.number].filter(Boolean).join(" ")}
                      labelLines={1}
                      onClick={() => openCard(card.cardSerialNo)}
                      startContent={<Text weight="bold" color="secondary">{i + 1}</Text>}
                      endContent={
                        // ☆ 즉시 추가 — **담기 시트**를 연다 (design SC-012 · 2026-09-03).
                        // 종전에는 전체화면 저장 폼으로 보냈는데, 목록에서 한 장 담는
                        // 손짓에 화면을 통째로 갈아 끼울 이유가 없다. 홈은 그대로 남는다.
                        // ⚠ 비회원은 저장이 막혀 있으므로(§6.1) 시트 대신 로그인부터 띄운다 —
                        //   종전엔 저장 폼 라우트의 RequireAuth 가 그 일을 했다.
                        <Button
                          variant="ghost"
                          size="sm"
                          label={t("home.quickAdd")}
                          clickAction={() => {
                            if (!isAuthenticated) {
                              requireAuth({ type: "none" });
                              return;
                            }
                            setAddSheetCard({ serialNo: card.cardSerialNo, name: card.name });
                          }}
                        />
                      }
                    />
                  ))}
                </List>
              </VStack>
            )}
          </VStack>
        )}

        {/* ── §2 요즘 많이 모으는 카드 (FN-048) ──────────────────── */}
        <VStack gap={3}>
          <HStack gap={2} justify="between" align="center" wrap="wrap">
            <Heading level={3}>{t("home.trendingTitle")}</Heading>
            <Button
              variant="ghost"
              size="sm"
              label={t("home.seeMore")}
              clickAction={() => setIsTrendSheetOpen(true)}
            />
          </HStack>

          <HStack gap={1} wrap="wrap">
            {TREND_CHIPS.map((c) => (
              <ToggleButton
                key={c}
                size="sm"
                data-compact=""
                label={t(`home.trendChip.${c.toLowerCase()}`)}
                isPressed={chip === c}
                onPressedChange={() => setChip(c)}
              />
            ))}
          </HStack>

          {/* 목록 면을 좌우로 밀면 칩이 넘어간다 — 칩 줄이 아니라 목록만 감싼다(위 캐러셀과 분리) */}
          <SwipeTabPanel tabs={TREND_CHIPS} activeTab={chip} onTabChange={setChip}>
          {trending.isPending ? (
            <VStack gap={2} aria-hidden="true">
              {[0, 1, 2].map((i) => <Skeleton key={i} height={56} />)}
            </VStack>
          ) : !trending.isSuccess || trending.data.length === 0 ? (
            // 가격 미적재 환경에서는 랭킹 근거가 없어 비어 있는 것이 정답이다
            <Text color="secondary">{t("home.trendEmpty")}</Text>
          ) : (
            <>
              <List hasDividers>
                {trending.data.slice(0, TREND_PREVIEW_COUNT).map(trendRow)}
                {/* 인피드 광고 — 홈의 유일한 광고 자리 (수익모델 §3.1).
                    §2 목록의 **마지막 줄**이다. 섹션 사이에 따로 떨어뜨리면
                    어느 쪽에도 안 속한 상자로 떠 보인다 (design 2026-08-27).
                    ⚠ 경계는 지킨다: 광고는 §3(내 관심 카드=내 자산) **위**에서 멈춘다. */}
                <InFeedAd />
              </List>
              {/* 📌 "호가는 참고용" 안내는 2026-08-20 에 뺐다 (design). 이 목록에는
                  매수·매도 버튼이 애초에 없어서 안 하겠다고 굳이 말할 자리가 아니었다.
                  문구(home.tradeDisabledNotice)는 남겨 뒀다 — 실제로 호가를 보여 주는
                  카드 상세 '지금 거래하면' 쪽에 필요해지면 그리로 옮기면 된다. */}
            </>
          )}
          </SwipeTabPanel>

          {/* ⏳ 광고 PoC (2026-09-09) — GPT fluid 가 이 자리에서 되는지 실측.
              자리는 기획 §3.1 의 홈 광고 위치(§2 와 §3 사이)와 같게 두었다.
              ⚠ SwipeTabPanel **밖**이다 — 안에 두면 trending 성공 분기에만 걸려
                백엔드가 죽은 기기에서는 프로브 자체가 안 뜬다(판정 불가).
                광고가 뜨는지를 재는 프로브가 우리 API 에 의존하면 안 된다.
              ⚠ 게이트가 DEV 가 아니라 VITE_ADS_PROBE 인 이유: 판정은 **빌드된 APK**
                에서 해야 하는데(웹뷰 origin·registerWebView 가 그때만 실물이다)
                vite build 는 import.meta.env.DEV 가 false 라 DEV 게이트로는 영영 안 뜬다.
              결론 나면 이 블록과 GptFluidProbe.tsx, .env.applocal 의 플래그를 함께 지운다. */}
          {import.meta.env.VITE_ADS_PROBE === "true" && <GptFluidProbe />}
        </VStack>

        {/* ── §3 내 관심 카드 = 컬렉션에 담은 카드 (FN-049) ─────────
            ⚠ 카드 캐러셀만 둔다 (2026-09-17 사용자 결정). 종전엔 위에 컬렉션 요약
              (카드수·시세합계·평가손익)을 한 칸 더 냈는데, 같은 숫자를 컬렉션 탭이
              화면 머리말로 이미 말하고 있어 홈에서는 걷었다. 이탈 경로는 제목 줄의
              '전체보기' 하나로 충분하다. */}
        <VStack gap={3}>
          {/* 제목 줄에 '전체보기' — §2 의 '더보기' 와 같은 자리·같은 모양이다.
              가는 곳은 **컬렉션 목록(SC-002)** 이다. 화면정의서가 §3 을 '컬렉션에
              담은 카드'로 재정의하면서 이탈 경로도 컬렉션 쪽으로 정해 두었다 —
              관심 카드를 따로 모아 놓은 화면은 따로 없다. */}
          <HStack gap={2} justify="between" align="center" wrap="wrap">
            <Heading level={3}>{t("home.watchlist")}</Heading>
            <Button
              variant="ghost"
              size="sm"
              label={t("home.seeAll")}
              clickAction={() => navigate("/collections")}
            />
          </HStack>
          {!isAuthenticated || watchCards.length === 0 ? (
            <Card padding={5}>
              <VStack gap={3} align="start">
                <Text color="secondary">{t("home.emptyWatchlist")}</Text>
                <Button variant="secondary" label={t("home.addBySearch")} clickAction={() => navigate("/search")} />
              </VStack>
            </Card>
          ) : (
            <Carousel gap={3} hasSnap hasEdgeFade className={styles.tileTop}>
              {watchCards.map(carouselCard)}
            </Carousel>
          )}
        </VStack>

        {/* ── §4 최근 스캔한 카드 (FN-050) ───────────────────────── */}
        <VStack gap={3}>
          <Heading level={3}>{t("home.recentScans")}</Heading>
          {recentIds.length === 0 || !recentScans.isSuccess || recentScans.data.length === 0 ? (
            <Card padding={5}>
              <VStack gap={3} align="start">
                <Text color="secondary">{t("home.emptyRecent")}</Text>
                <Button variant="primary" label={t("home.goScan")} clickAction={() => overlayNavigate("/scan")} />
              </VStack>
            </Card>
          ) : (
            <VStack gap={2}>
              {/* 한 칸이 본문 폭을 다 쓴다 — 모바일에서 한 번에 한 건.
                  다음 칸이 화면 끝에 살짝 걸쳐 옆에 더 있다는 게 보인다
                  (.tileTop 의 전폭 처리 + .scanRail 의 칸 폭). */}
              <Carousel
                gap={3}
                hasSnap
                hasEdgeFade
                className={`${styles.tileTop} ${styles.scanRail}`}
                aria-label={t("home.recentScans")}
              >
                {recentScans.data.map(recentScanSlide)}
              </Carousel>

            </VStack>
          )}
        </VStack>
      </VStack>

      {/* SC-019a — §2 '더보기' 바텀시트. 홈에서 고른 칩을 이어받는다 (design) */}
      <TrendingAllSheet
        open={isTrendSheetOpen}
        onClose={() => setIsTrendSheetOpen(false)}
        initialChip={chip}
        chips={TREND_CHIPS}
      />

      {/* 담기 시트 — 트렌딩 목록의 '담기'. 홈은 그대로 남고 담고 나면 제자리다. */}
      {addSheetCard && (
        <AddToCollectionSheet
          open
          /* 트렌딩에서 담는 것은 관심이지 보유가 아니다 (IA rev27) */
          target="collection"
          onClose={() => setAddSheetCard(null)}
          cardSerialNo={addSheetCard.serialNo}
          cardName={addSheetCard.name}
        />
      )}
    </Section>
  );
}
